import { ListFieldValueTypename, MapFieldValueTypename, StringFieldValueTypename } from '../../../__generated__/chirp/com/hubspot/breeze/engine/rpc/BreezeEngineStreamingService';
import { ImageGridResponseSectionTypename, SourcesListResponseSectionTypename } from '../../../__generated__/chirp/com/hubspot/copilot/rpc/v2/CopilotMessageService';
import { convertFieldValueAvroToJson, convertJsonToFieldValueAvro } from 'rpc-client-utils';
import { parseArtifactPayload } from 'breeze-engine-core';
// BZE tool payloads omit the FQ __typename and only include a short `type`
// field (e.g. "IMAGE_GRID"). Map known short names to their FQ typenames so
// DisplayOnlyEntry.__typename is consistent regardless of delivery path.
const BZE_SHORT_TYPE_TO_TYPENAME = {
  IMAGE_GRID: ImageGridResponseSectionTypename,
  SOURCES_LIST: SourcesListResponseSectionTypename
};
function isListFieldValue(value) {
  return value.__typename === ListFieldValueTypename;
}
function isMapFieldValue(value) {
  return value.__typename === MapFieldValueTypename;
}
function isStringFieldValue(value) {
  return value.__typename === StringFieldValueTypename;
}
function getString(map, key) {
  const field = map.value[key];
  if (field && isStringFieldValue(field)) {
    return field.value;
  }
  return '';
}
function toDisplayOnlyEntry(value, citationId) {
  var _BZE_SHORT_TYPE_TO_TY;
  const shortType = getString(value, '__typename') || getString(value, '_type') || (citationId !== undefined ? getString(value, 'type') : '');
  const typename = (_BZE_SHORT_TYPE_TO_TY = BZE_SHORT_TYPE_TO_TYPENAME[shortType]) !== null && _BZE_SHORT_TYPE_TO_TY !== void 0 ? _BZE_SHORT_TYPE_TO_TY : shortType;
  if (!typename) return null;
  const type = getString(value, 'type') || typename;
  return Object.assign({
    __typename: typename,
    type,
    id: getString(value, 'id')
  }, citationId !== undefined && {
    citationId
  }, {
    value: convertFieldValueAvroToJson(value)
  });
}
function parseEntry(entryField) {
  if (!isMapFieldValue(entryField)) return null;
  return toDisplayOnlyEntry(entryField);
}
export function parseCitationDisplay(value, citationId) {
  if (!isMapFieldValue(value)) return null;
  return toDisplayOnlyEntry(value, citationId);
}
export const BreezeEngineUrlCitationTypename = 'com.hubspot.breeze.engine.tool.utils.BreezeEngineUrlCitation';
export function parseDisplayOnlyUrlCitationBlock(value) {
  if (!isMapFieldValue(value)) return null;
  if (getString(value, '__typename') !== BreezeEngineUrlCitationTypename) return null;
  if (!getString(value, 'url')) return null;
  return {
    __typename: BreezeEngineUrlCitationTypename,
    type: getString(value, 'type') || BreezeEngineUrlCitationTypename,
    id: getString(value, 'id'),
    value: convertFieldValueAvroToJson(value)
  };
}
export function parseInlineDisplayContent(value, displayType) {
  if (displayType !== 'inline') {
    return null;
  }
  if (!isMapFieldValue(value)) {
    return null;
  }
  const mimeType = getString(value, 'mimeType');
  const data = getString(value, 'data');
  if (!mimeType || !data) {
    return null;
  }
  if (mimeType.startsWith('image/')) {
    return {
      type: 'IMAGE',
      base64Data: data,
      mimeType
    };
  }
  if (mimeType === 'text/html') {
    return {
      type: 'INLINE_DISPLAY',
      mimeType,
      data
    };
  }
  return {
    type: 'TEXT',
    text: data
  };
}
export function parseDisplayOnlyEntries(value) {
  var _value$value;
  if (!isListFieldValue(value)) {
    return null;
  }
  const items = (_value$value = value.value) !== null && _value$value !== void 0 ? _value$value : [];
  const result = [];
  for (const item of items) {
    const parsed = parseEntry(item);
    if (parsed !== null) {
      result.push(parsed);
    }
  }
  return result;
}
function isAvroFieldValue(value) {
  if (typeof value !== 'object' || value === null) return false;
  const typename = value.__typename;
  // Avro FieldValue types are in com.hubspot.chirp.ext.models.*
  return typeof typename === 'string' && typename.startsWith('com.hubspot.chirp.ext.models.');
}

// Maps numeric BreezeArtifactType ordinals (Java enum) to string keys.
// The BE serializes the enum as an integer in Avro; this boundary conversion
// normalizes it once so all downstream code works with strings only.
const NUMERIC_ARTIFACT_TYPE_MAP = {
  0: 'MARKDOWN',
  1: 'HTML',
  2: 'CODE',
  3: 'CSV',
  4: 'TEXT',
  5: 'EMAIL',
  6: 'LANDING_PAGE',
  7: 'BLOG_POST',
  8: 'SITE_PAGE',
  9: 'WORKFLOW',
  10: 'SMS',
  11: 'REPORT',
  12: 'CRM_LIST',
  13: 'WORKSPACE',
  15: 'DATA_MODEL',
  16: 'FORMS_CONDITIONAL_LOGIC'
};
function resolveArtifactType(raw) {
  var _NUMERIC_ARTIFACT_TYP;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number') return (_NUMERIC_ARTIFACT_TYP = NUMERIC_ARTIFACT_TYPE_MAP[raw]) !== null && _NUMERIC_ARTIFACT_TYP !== void 0 ? _NUMERIC_ARTIFACT_TYP : '';
  return '';
}
function parseArtifactBlock(fieldValue) {
  var _ref;
  if (!isMapFieldValue(fieldValue)) return null;
  const decoded = convertFieldValueAvroToJson(fieldValue);
  // CHIRP TS client double-encodes FieldValue fields — we pre-encode with
  // convertJsonToFieldValueAvro because the client can't serialize the raw JSON,
  // then the client wraps it again. Unwrap the extra layer here.
  const data = typeof decoded.name === 'string' ? decoded : (_ref = decoded.value) !== null && _ref !== void 0 ? _ref : decoded;
  const name = typeof data.name === 'string' ? data.name : '';
  const artifactId = typeof data.artifactId === 'number' ? data.artifactId : 0;
  if (!name || !artifactId) return null;
  const payload = parseArtifactPayload(data.payload);
  return Object.assign({
    type: 'ARTIFACT',
    artifactId
  }, typeof data.versionId === 'number' && {
    versionId: data.versionId
  }, typeof data.version === 'number' && {
    version: data.version
  }, {
    artifactType: typeof data.artifactTypeName === 'string' && data.artifactTypeName ? data.artifactTypeName : resolveArtifactType(data.artifactType),
    name,
    path: typeof data.path === 'string' ? data.path : '',
    action: typeof data.action === 'string' ? data.action : '',
    source: 'display-block'
  }, payload !== undefined && {
    payload
  });
}
function parseFileReferencesBlock(fieldValue) {
  if (!isListFieldValue(fieldValue)) return null;
  const fileReferences = [];
  for (const item of (_fieldValue$value = fieldValue.value) !== null && _fieldValue$value !== void 0 ? _fieldValue$value : []) {
    var _fieldValue$value;
    if (!isMapFieldValue(item)) continue;
    const fileId = getString(item, 'fileId');
    if (!fileId) continue;
    fileReferences.push({
      fileId,
      fileName: getString(item, 'fileName')
    });
  }
  if (!fileReferences.length) return null;
  return {
    type: 'FILE_REFERENCES',
    fileReferences
  };
}
export function parseDisplayOnlyBlock(block) {
  if (!block.value) return null;
  // Ably delivers DISPLAY_ONLY block values as plain JSON (array for list
  // entries, object for single-entry citation blocks). CHIRP history delivers
  // them as proper Avro FieldValues. Convert to Avro when needed so both
  // paths hit the same parsing logic.
  const needsConversion = Array.isArray(block.value) || !isAvroFieldValue(block.value);
  const fieldValue = needsConversion ? convertJsonToFieldValueAvro(block.value) : block.value;
  if (block.citationId) {
    const entry = parseCitationDisplay(fieldValue, block.citationId);
    if (entry) return {
      type: 'DISPLAY_ONLY',
      entries: [entry]
    };
    return null;
  }
  if (block.displayType === 'artifact') {
    return parseArtifactBlock(fieldValue);
  }
  if (block.displayType === 'file-references') {
    return parseFileReferencesBlock(fieldValue);
  }
  const entries = parseDisplayOnlyEntries(fieldValue);
  if (entries !== null) return {
    type: 'DISPLAY_ONLY',
    entries
  };
  const citationEntry = parseDisplayOnlyUrlCitationBlock(fieldValue);
  if (citationEntry) return {
    type: 'DISPLAY_ONLY',
    entries: [citationEntry]
  };
  const inlineContent = parseInlineDisplayContent(fieldValue, block.displayType);
  if (inlineContent) return inlineContent;

  // Some sections (e.g. SourcesListResponseSection) arrive as a top-level MAP
  // rather than a LIST. The checks above only handle specific known MAP shapes
  // (companion citationId, URL citation, inline content). This catches the
  // general case so the section registry can render them instead of showing
  // a raw JSON dump.
  if (isMapFieldValue(fieldValue)) {
    const standaloneEntry = toDisplayOnlyEntry(fieldValue);
    if (standaloneEntry) return {
      type: 'DISPLAY_ONLY',
      entries: [standaloneEntry]
    };
  }
  return {
    type: 'DISPLAY_ONLY',
    text: JSON.stringify(convertFieldValueAvroToJson(fieldValue))
  };
}